/**
 * Role Access Utilities
 * Shared capability checks by Strapi role type.
 */

import {
  type RoleType,
  ADMIN_PORTAL_ROLE_TYPES,
  SYSTEM_USER_ROLE_TYPES,
  VISITOR_MANAGE_ROLE_TYPES,
  FINANCE_ADMIN_ALLOWED_ROUTE_PREFIXES,
  ADMIN_ROLE_RESTRICTED_ROUTES,
  ROLE_TYPES,
  GATE_FACTORY_SELECTOR_DENIED_ROLE_TYPES,
  GATE_PORTAL_EMPLOYEE_ALLOWED_ROUTES,
  GATE_PORTAL_SERVICES_MANAGER_ALLOWED_ROUTES,
  GATE_PORTAL_SECURITY_GUARD_ALLOWED_ROUTES,
  GATE_PORTAL_SCALE_ALLOWED_ROUTES,
  GATE_PORTAL_PACKHOUSE_MANAGER_ALLOWED_ROUTES,
} from './role-constants';

export function isSalesOfficeUserRole(roleType: RoleType): boolean {
  return roleType === ROLE_TYPES.SALES_OFFICE_USER;
}

export function canAccessAdminPortal(roleType: RoleType): boolean {
  return ADMIN_PORTAL_ROLE_TYPES.includes(roleType as any);
}

export function canManageSystemUsers(roleType: RoleType): boolean {
  return roleType === ROLE_TYPES.SUPER_ADMIN || roleType === ROLE_TYPES.ADMIN;
}

export function canControlGates(roleType: RoleType): boolean {
  return !isSalesOfficeUserRole(roleType);
}

export function canManageOrganizationData(roleType: RoleType): boolean {
  return roleType !== ROLE_TYPES.FINANCE_ADMIN;
}

export function isSystemUserRole(roleType: RoleType): boolean {
  return SYSTEM_USER_ROLE_TYPES.includes(roleType as any);
}

export function canAccessAdminRoute(
  roleType: RoleType,
  pathname: string,
): boolean {
  if (!pathname) return false;

  if (roleType === ROLE_TYPES.ADMIN) {
    return !ADMIN_ROLE_RESTRICTED_ROUTES.some(
      (route) => pathname === route || pathname.startsWith(`${route}/`),
    );
  }

  if (roleType !== ROLE_TYPES.FINANCE_ADMIN) return true;

  return FINANCE_ADMIN_ALLOWED_ROUTE_PREFIXES.some(
    (prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`),
  );
}

export function getDefaultAdminRouteForRole(roleType: RoleType): string {
  if (roleType === ROLE_TYPES.FINANCE_ADMIN) return '/finance/overview';
  return '/';
}

export function canManageVisitors(roleType: RoleType): boolean {
  return VISITOR_MANAGE_ROLE_TYPES.includes(roleType as any);
}

export function canSelectGateFactory(roleType: RoleType): boolean {
  if (!roleType) return false;
  return !GATE_FACTORY_SELECTOR_DENIED_ROLE_TYPES.includes(roleType as any);
}

export function isEmployeeRole(roleType: RoleType): boolean {
  return roleType === ROLE_TYPES.EMPLOYEE;
}

export function isSafetyManagerRole(roleType: RoleType): boolean {
  return roleType === ROLE_TYPES.SAFETY_MANAGER;
}

export function isServicesManagerRole(roleType: RoleType): boolean {
  return roleType === ROLE_TYPES.SERVICES_MANAGER;
}

export function isSecurityGuardRole(roleType: RoleType): boolean {
  return roleType === ROLE_TYPES.SECURITY_GUARD;
}

export function canEmployeeAccessRoute(pathname: string): boolean {
  if (!pathname) return false;
  return GATE_PORTAL_EMPLOYEE_ALLOWED_ROUTES.some(
    (route) => pathname === route || pathname.startsWith(`${route}/`),
  );
}

export function canServicesManagerAccessRoute(pathname: string): boolean {
  if (!pathname) return false;
  return GATE_PORTAL_SERVICES_MANAGER_ALLOWED_ROUTES.some(
    (route) => pathname === route || pathname.startsWith(`${route}/`),
  );
}

export function canSecurityGuardAccessRoute(pathname: string): boolean {
  if (!pathname) return false;
  return GATE_PORTAL_SECURITY_GUARD_ALLOWED_ROUTES.some(
    (route) => pathname === route || pathname.startsWith(`${route}/`),
  );
}

export function isScaleOperatorRole(roleType: RoleType): boolean {
  return roleType === ROLE_TYPES.SCALE_OPERATOR;
}

export function isScaleAdminRole(roleType: RoleType): boolean {
  return roleType === ROLE_TYPES.SCALE_ADMIN;
}

export function isScaleRole(roleType: RoleType): boolean {
  return isScaleOperatorRole(roleType) || isScaleAdminRole(roleType);
}

export function canScaleRoleAccessRoute(pathname: string): boolean {
  if (!pathname) return false;
  return GATE_PORTAL_SCALE_ALLOWED_ROUTES.some(
    (route) => pathname === route || pathname.startsWith(`${route}/`),
  );
}

export function getDefaultGateRouteForRole(roleType: RoleType): string {
  if (isScaleRole(roleType)) return '/weighbridge-ops';
  return '/';
}

export function isPackhouseManagerRole(roleType: RoleType): boolean {
  return roleType === ROLE_TYPES.PACKHOUSE_MANAGER;
}

export function canPackhouseManagerAccessRoute(pathname: string): boolean {
  if (!pathname) return false;
  return GATE_PORTAL_PACKHOUSE_MANAGER_ALLOWED_ROUTES.some(
    (route) => pathname === route || pathname.startsWith(`${route}/`),
  );
}
